import { useEffect, useRef, useCallback } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_ENDPOINTS } from 'config/ApiConfig';
import useAuthStore from 'Store/useAuthStore';
import { isTokenValid } from 'Utils/tokenValidation';

/**
 * SSE hook for HRIS employee onboarding job progress.
 *
 * Mirrors the pattern in useHrisUploadSSE but is driven by props instead of
 * a global store, so it can be used inline in the mapping results component.
 *
 * Flow:
 *  1. Component POSTs to onboard-mapped-employees → receives 202 { dispatched: true }.
 *  2. Component sets onboardStatus = 'streaming', which activates this hook (isActive=true).
 *  3. Hook opens SSE stream to HRIS_STREAM/{uploadId} — same endpoint as mapping progress.
 *  4. Each event fires onProgress(data) so the component can update its progress UI.
 *  5. On terminal status the hook fires onTerminal(data) and closes the stream.
 *  6. key_does_not_exist / connection errors are retried up to MAX_SSE_RETRIES times;
 *     exhaustion fires onRetryExhausted() so the component can show an error state.
 *  7. Stream is always closed on component unmount via the useEffect cleanup.
 */

// Completion events — gated on event name so stale mapping stream data
// (status:'Task Completed', different event name) never fires the onboarding handler.
const ONBOARD_TERMINAL_EVENTS = new Set([
  'hris_onboarding_completed',
  'hris_onboarding_failed',
]);

// Error statuses checked as a secondary gate: these never appear as false-positives
// in old mapping stream data, so a status-string check is safe here.
const ONBOARD_ERROR_STATUSES = new Set([
  'HRIS Onboarding Failed',
  'HRIS Mapping Failed',
]);

const MAX_SSE_RETRIES = 3;
const SSE_RETRY_DELAY_MS = 2000;

const useHrisOnboardSSE = ({ uploadId, isActive, onProgress, onTerminal, onRetryExhausted }) => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const esRef       = useRef(null);
  const cancelledRef = useRef(false);
  const retryCountRef = useRef(0);

  // Keep callback refs current without forcing connectSSE to rebuild
  const onProgressRef        = useRef(onProgress);
  const onTerminalRef        = useRef(onTerminal);
  const onRetryExhaustedRef  = useRef(onRetryExhausted);
  useEffect(() => { onProgressRef.current       = onProgress;       }, [onProgress]);
  useEffect(() => { onTerminalRef.current        = onTerminal;        }, [onTerminal]);
  useEffect(() => { onRetryExhaustedRef.current  = onRetryExhausted;  }, [onRetryExhausted]);

  const connectSSE = useCallback(() => {
    if (cancelledRef.current || !uploadId || !accessToken) return;
    if (!isTokenValid(accessToken, 'useHrisOnboardSSE')) return;

    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }

    const es = new EventSourcePolyfill(`${API_ENDPOINTS.HRIS_STREAM}${uploadId}?type=onboarding`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      heartbeatTimeout: 60 * 60 * 1000, // onboarding jobs can run several minutes
    });
    esRef.current = es;

    es.onmessage = (event) => {
      if (cancelledRef.current) return;
      try {
        let data = JSON.parse(event.data);

        // Unwrap double-wrapped format: {"data": "{\"status\":...}"}
        if (data.data && typeof data.data === 'string' && !data.status) {
          try { data = JSON.parse(data.data); } catch (_) { /* use outer as-is */ }
        }

        // Redis stream not yet created — Cloud Run Job hasn't started writing
        if (data.event === 'key_does_not_exist') {
          es.close();
          esRef.current = null;
          if (retryCountRef.current < MAX_SSE_RETRIES) {
            retryCountRef.current += 1;
            setTimeout(connectSSE, SSE_RETRY_DELAY_MS);
          } else {
            onRetryExhaustedRef.current?.();
          }
          return;
        }

        retryCountRef.current = 0;

        const isTerminal = ONBOARD_TERMINAL_EVENTS.has(data.event)
          || ONBOARD_ERROR_STATUSES.has(data.status);

        if (isTerminal) {
          es.close();
          esRef.current = null;
          onTerminalRef.current?.(data);
        } else {
          // Non-terminal event (hris_onboarding_started, hris_onboarding_progress, etc.)
          onProgressRef.current?.(data);
        }
      } catch (err) {
        console.error('[HrisOnboardSSE] parse error:', err);
      }
    };

    es.onerror = () => {
      if (cancelledRef.current) return;
      es.close();
      esRef.current = null;
      if (retryCountRef.current < MAX_SSE_RETRIES) {
        retryCountRef.current += 1;
        setTimeout(connectSSE, SSE_RETRY_DELAY_MS);
      } else {
        onRetryExhaustedRef.current?.();
      }
    };

    es.addEventListener('end', () => {
      es.close();
      esRef.current = null;
    });
  }, [uploadId, accessToken]);

  useEffect(() => {
    if (!isActive || !uploadId || !accessToken) return;

    cancelledRef.current = false;
    retryCountRef.current = 0;
    connectSSE();

    return () => {
      cancelledRef.current = true;
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
    };
  }, [isActive, uploadId, accessToken, connectSSE]);
};

export default useHrisOnboardSSE;
